"use client";

/**
 * train-memory-growth.tsx — Done 页「记忆成长」
 *
 * §0 纯视觉:5 段记忆条(stability → 段数),旧段翡翠、新段暖金从旧位置生长;
 * 本组答错的概念 → 缩短染玫红(不指责),0 段时保底 1 段玫红「遇到过」签名;
 * 「N 天后再见」= nextReviewAt 距今。
 * 按 conceptKey 聚合(一概念可多题);最多 4 行,超出折叠「+N」。
 */

import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/cn";
import { DISPLAY_FONT, EASE_OUT } from "@/components/dojo-theme";
import { conceptKeyZh } from "@/lib/poker-terms-zh";
import type { SessionResult } from "@/lib/session-store";

/** stability(天)→ 0-5 段:0.5 / 1.5 / 3.5 / 8 / 16 天门槛 */
const SEG_THRESHOLDS = [0.5, 1.5, 3.5, 8, 16];

function stabilitySegs(s: number | null): number {
  if (s == null || !Number.isFinite(s)) return 0;
  return SEG_THRESHOLDS.filter((t) => s >= t).length;
}

function daysLabel(ts: number | null): string {
  if (ts == null) return "明天";
  const days = Math.round((ts - Date.now()) / 86_400_000);
  return days <= 0 ? "明天" : `${days} 天后再见`;
}

type ConceptRow = {
  key: string;
  before: number | null;
  after: number | null;
  nextReviewAt: number | null;
  missed: boolean;
  count: number;
};

function MemoryBar({ row, reduce }: { row: ConceptRow; reduce: boolean }) {
  const beforeSegs = stabilitySegs(row.before);
  const missed = row.missed;
  // 答错签名:stability 未过门槛时本会 0 段整行空白 → 保底 1 段玫红 =「遇到过」
  const afterSegs = missed
    ? Math.max(1, stabilitySegs(row.after))
    : stabilitySegs(row.after);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-4 py-3",
        missed ? "border-rose-400/20 bg-[#150F11]/80" : "border-[#1E2E23] bg-[#0D1510]/80",
      )}
    >
      {/* 概念中文 */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-stone-200">
          {conceptKeyZh(row.key)}
        </div>
        {row.count > 1 && (
          <div className="mt-0.5 font-mono text-[10px] tabular-nums text-stone-500">
            ×{row.count} 题
          </div>
        )}
      </div>

      {/* 5 段记忆条:旧段翡翠,新段暖金生长;答错 → 缩短染玫红 */}
      <div className="flex w-24 shrink-0 gap-1 md:w-32" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => {
          const isGrown = i >= beforeSegs && i < afterSegs;
          const isLost = i >= afterSegs && i < beforeSegs;
          const isEmpty = i >= afterSegs && i >= beforeSegs;

          if (isGrown) {
            return (
              <motion.span
                key={i}
                className={cn(
                  "h-2.5 flex-1 origin-left rounded-full",
                  missed
                    ? "bg-rose-400/85"
                    : "bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.4)]",
                )}
                initial={reduce ? false : { scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : {
                        type: "spring",
                        stiffness: 220,
                        damping: 22,
                        delay: 0.55 + (i - beforeSegs) * 0.12,
                      }
                }
              />
            );
          }

          if (isLost) {
            // 缩短:从旧高度抽走,一次性暗下去(不做红色指责)
            return (
              <motion.span
                key={i}
                className="h-2.5 flex-1 rounded-full bg-[#223329]"
                initial={reduce ? false : { backgroundColor: "rgba(251,113,133,0.45)" }}
                animate={{ backgroundColor: "rgba(34,51,41,1)" }}
                transition={reduce ? { duration: 0 } : { duration: 0.7, delay: 0.5 }}
              />
            );
          }

          return (
            <span
              key={i}
              className={cn(
                "h-2.5 flex-1 rounded-full",
                isEmpty ? "bg-[#223329]" : missed ? "bg-rose-400/85" : "bg-emerald-400/50",
              )}
            />
          );
        })}
      </div>

      {/* N 天后再见 */}
      <span
        className={cn(
          DISPLAY_FONT,
          "w-[4.5rem] shrink-0 text-right text-sm font-bold tracking-tight",
          missed ? "text-rose-300/80" : "text-amber-300/90",
        )}
      >
        {daysLabel(row.nextReviewAt)}
      </span>
    </div>
  );
}

export function MemoryGrowth({ results }: { results: SessionResult[] }) {
  const reduce = useReducedMotion() ?? false;
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    const map = new Map<string, ConceptRow>();
    for (const r of results) {
      if (!r.conceptKey) continue;
      const cur = map.get(r.conceptKey);
      if (!cur) {
        map.set(r.conceptKey, {
          key: r.conceptKey,
          before: r.stabilityBefore,
          after: r.stabilityAfter,
          nextReviewAt: r.nextReviewAt,
          missed: !r.correct,
          count: 1,
        });
      } else {
        cur.after = r.stabilityAfter;
        cur.nextReviewAt = r.nextReviewAt;
        cur.missed = cur.missed || !r.correct;
        cur.count += 1;
      }
    }
    // 记忆最强的在前,答错的沉底(正向优先)
    return [...map.values()].sort((a, b) => (b.after ?? 0) - (a.after ?? 0));
  }, [results]);

  if (rows.length === 0) return null;

  const hidden = rows.length - 4;

  return (
    <section className="space-y-2.5">
      <h2 className="text-sm font-semibold text-stone-200">记忆</h2>
      {rows.slice(0, 4).map((row) => (
        <MemoryBar key={row.key} row={row} reduce={reduce} />
      ))}
      {hidden > 0 && (
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="more"
              className="space-y-2.5 overflow-hidden"
              initial={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: EASE_OUT }}
            >
              {rows.slice(4).map((row) => (
                <MemoryBar key={row.key} row={row} reduce={reduce} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full rounded-lg py-1 text-center font-mono text-xs tabular-nums text-stone-500 transition-colors hover:text-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60"
        >
          {expanded ? "收起" : `+${hidden}`}
        </button>
      )}
    </section>
  );
}
